import { Link } from "react-router-dom";
import PageLayout from "@/components/aoria/PageLayout";
import PageHeader from "@/components/aoria/PageHeader";
import Seo from "@/components/aoria/Seo";

/**
 * Six situations concrètes — chaque cas s'articule en quatre dimensions :
 *   - situation : le contexte vécu
 *   - difficulte : ce qui résiste cognitivement
 *   - apport    : ce qu'un outil bien employé peut soulager
 *   - limites   : ce qu'il faut conserver à l'esprit
 *
 * Volontairement : aucun nom de produit, aucune marque. La page parle
 * d'usages et d'accessibilité cognitive, pas de technologie.
 */
const SITUATIONS = [
    {
        n: "01",
        title: "Comprendre un courrier administratif",
        situation:
            "Recevoir un courrier dense, parfois ambigu, dont l'objet réel et les démarches attendues ne sautent pas aux yeux.",
        difficulte:
            "Vocabulaire spécialisé, références à des codes ou des articles, formulations indirectes : la charge de lecture est élevée alors qu'une action est attendue dans un délai donné.",
        apport:
            "Reformuler le courrier en langage simple, faire ressortir l'objet réel, isoler les démarches demandées et les pièces à fournir.",
        limites:
            "La reformulation peut perdre une nuance juridique. Pour un sujet sensible (droits, santé, finances), une vérification humaine compétente reste nécessaire.",
    },
    {
        n: "02",
        title: "Préparer une réponse",
        situation:
            "Devoir répondre à un courrier, un message professionnel ou une demande officielle sans savoir par où commencer.",
        difficulte:
            "Trouver le bon registre, structurer l'information, ne rien oublier d'important, éviter une formulation maladroite qui se retournerait contre soi.",
        apport:
            "Construire un premier brouillon structuré, organiser les arguments, proposer plusieurs registres possibles selon le destinataire.",
        limites:
            "Le brouillon reste à relire et à ajuster. La voix personnelle, le contexte précis et la décision finale appartiennent toujours à la personne qui signe.",
    },
    {
        n: "03",
        title: "Rendre un texte plus accessible",
        situation:
            "Devoir transmettre une information dense (note interne, mode d'emploi, document de référence) à des personnes qui ne le liront pas si elles n'y comprennent rien.",
        difficulte:
            "Adapter le niveau de langue sans dénaturer le sens, condenser sans appauvrir, expliciter ce qui était implicite pour l'auteur initial.",
        apport:
            "Produire une version simplifiée, suggérer un lexique plus accessible, mettre en relief la structure logique du texte.",
        limites:
            "Une version simplifiée n'est pas une version « vraie ». Pour des contenus officiels, la version d'origine doit rester la référence.",
    },
    {
        n: "04",
        title: "Organiser ses idées",
        situation:
            "Avoir une masse de notes, d'idées et d'informations dispersées, sans parvenir à dégager un plan ou une liste d'actions claires.",
        difficulte:
            "La fonction exécutive cognitive (hiérarchiser, ordonner, décider de la suite) peut se trouver saturée, notamment en période de fatigue ou de stress.",
        apport:
            "Passer d'un brouillon désordonné à un plan lisible, faire émerger les liens implicites, transformer des notes en liste d'actions concrètes.",
        limites:
            "L'outil propose une organisation, il ne décide pas des priorités. Le choix de ce qui compte vraiment reste un acte humain.",
    },
    {
        n: "05",
        title: "Préparer une réunion ou un entretien",
        situation:
            "Devoir préparer une rencontre (réunion professionnelle, entretien d'embauche, rendez-vous médical, échange institutionnel) sans laisser passer ce qui sera important.",
        difficulte:
            "Identifier les points essentiels, anticiper les questions possibles, structurer ses arguments, rester calme face à un cadre qui peut être intimidant.",
        apport:
            "Faire émerger les points-clés à aborder, préparer des questions pertinentes, construire un plan d'intervention adapté à l'interlocuteur.",
        limites:
            "La rencontre humaine reste imprévisible. Une préparation utile ne remplace pas l'attention au moment présent ni l'écoute de l'autre.",
    },
    {
        n: "06",
        title: "Soutenir les apprentissages",
        situation:
            "Étudier une notion difficile, préparer un examen, accompagner un proche dans son travail scolaire, reprendre une formation après une coupure.",
        difficulte:
            "Une explication unique ne convient pas à tout le monde. La fatigue, le rythme imposé ou la complexité d'un manuel peuvent bloquer l'accès à un savoir pourtant accessible.",
        apport:
            "Reformuler une notion, proposer des exemples adaptés, varier les angles d'explication, créer des exercices de vérification.",
        limites:
            "Apprendre demande un effort propre, non délégable. L'outil aide à comprendre ; il ne se substitue ni au cours, ni à un enseignant, ni au temps de pratique.",
    },
];

const NON_REMPLACE = [
    {
        title: "L'esprit critique",
        body: "Évaluer ce qui est dit, vérifier ce qui est affirmé, distinguer une information solide d'une formulation seulement convaincante : ces opérations restent du ressort humain.",
    },
    {
        title: "La décision",
        body: "Choisir, arbitrer, assumer un choix : aucune assistance ne dispense d'une décision personnelle, qui engage celle ou celui qui la prend.",
    },
    {
        title: "L'accompagnement humain",
        body: "Une présence, une écoute, un échange ajusté à une situation singulière : aucun outil ne reproduit ce que peut faire une personne attentive.",
    },
    {
        title: "Les professionnels",
        body: "L'expertise des médecins, avocats, enseignants, travailleurs sociaux et ergothérapeutes reste irremplaçable, en particulier dès qu'une situation engage des droits, une santé ou une sécurité.",
    },
    {
        title: "L'apprentissage lui-même",
        body: "Comprendre durablement, mémoriser, transférer à d'autres situations : ces processus demandent du temps, de la pratique et un engagement personnel qui ne se délèguent pas.",
    },
];

export default function Outils() {
    return (
        <PageLayout testid="outils-page">
            <Seo
                title="Outils · Situations concrètes d'accessibilité cognitive · Aoria"
                description="Six situations concrètes où l'intelligence artificielle, bien utilisée, peut devenir un outil d'accessibilité cognitive : comprendre un courrier, préparer une réponse, organiser ses idées, soutenir les apprentissages. Avec leurs limites assumées."
                path="/outils"
            />
            <PageHeader
                testid="outils"
                eyebrow="OUTILS"
                title="Concrètement,"
                italic="à quoi cela peut-il servir ?"
                lead="Six situations de la vie ordinaire (administrative, professionnelle, scolaire, personnelle) où l'intelligence artificielle, utilisée avec discernement, peut alléger une charge cognitive et permettre à chacun de faire ce qu'il a à faire. Avec leurs limites assumées."
                backdrop="modules"
            />

            {/* Situations concrètes */}
            <section
                data-testid="outils-situations"
                className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-28"
            >
                <div className="flex items-center gap-4 mb-14">
                    <span
                        aria-hidden="true"
                        className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                    />
                    <span className="eyebrow">Situations concrètes</span>
                </div>

                <ol className="divide-y divide-[#E0E1DD] border-y border-[#E0E1DD]">
                    {SITUATIONS.map((s, i) => (
                        <li
                            key={s.n}
                            data-testid={`outils-situation-${i}`}
                            className="py-14 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
                        >
                            <div className="lg:col-span-3">
                                <span className="editorial-num text-3xl md:text-4xl text-[#0D1B2A]">
                                    {s.n}
                                </span>
                                <h2 className="h3 mt-6 text-xl md:text-2xl text-[#0D1B2A] leading-snug max-w-xs">
                                    {s.title}
                                </h2>
                            </div>
                            <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 max-w-4xl">
                                <div>
                                    <div className="eyebrow text-[#415A77]">
                                        Situation
                                    </div>
                                    <p className="mt-3 text-[#1B263B] text-base leading-relaxed">
                                        {s.situation}
                                    </p>
                                </div>
                                <div>
                                    <div className="eyebrow text-[#415A77]">
                                        Difficulté rencontrée
                                    </div>
                                    <p className="mt-3 text-[#1B263B] text-base leading-relaxed">
                                        {s.difficulte}
                                    </p>
                                </div>
                                <div>
                                    <div className="eyebrow text-[#0D1B2A]">
                                        Ce que l'outil peut apporter
                                    </div>
                                    <p className="mt-3 text-[#1B263B] text-base leading-relaxed">
                                        {s.apport}
                                    </p>
                                </div>
                                <div>
                                    <div className="eyebrow text-[#778DA9]">
                                        Limites
                                    </div>
                                    <p className="mt-3 text-[#5C6B7D] text-base leading-relaxed">
                                        {s.limites}
                                    </p>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>

            {/* Ce que l'IA ne remplace pas */}
            <section
                data-testid="outils-ne-remplace-pas"
                className="py-24 md:py-32 bg-[#F4F5F7] border-y border-[#E0E1DD]"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
                        <div className="lg:col-span-3">
                            <div className="reveal flex items-center gap-4">
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                                />
                                <span className="eyebrow">
                                    Ce que l'IA ne remplace pas
                                </span>
                            </div>
                        </div>
                        <div className="lg:col-span-9 max-w-3xl">
                            <h2 className="reveal delay-1 h2 text-3xl md:text-4xl text-[#0D1B2A] leading-tight">
                                Les usages utiles ont,{" "}
                                <span className="italic text-[#415A77]">
                                    en miroir, des limites assumées
                                </span>
                                .
                            </h2>
                            <p className="reveal delay-2 mt-8 text-[#1B263B] text-base md:text-lg leading-relaxed">
                                Présenter ces outils comme des outils
                                d'accessibilité cognitive suppose de dire avec
                                la même clarté ce qu'ils ne sont pas, ce qu'ils
                                ne font pas, et ce qu'ils ne devraient jamais
                                prétendre faire.
                            </p>
                        </div>
                    </div>

                    <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {NON_REMPLACE.map((item, i) => (
                            <li
                                key={item.title}
                                data-testid={`outils-non-remplace-${i}`}
                                className="card-aoria"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <span className="editorial-num text-sm">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="eyebrow text-[#778DA9]">
                                        Non délégable
                                    </span>
                                </div>
                                <h3 className="h3 text-lg md:text-xl text-[#0D1B2A] leading-snug">
                                    {item.title}
                                </h3>
                                <p className="mt-4 text-[#5C6B7D] text-sm md:text-base leading-relaxed flex-1">
                                    {item.body}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* CTAs vers FAQ et Ressources */}
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-24">
                <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                        to="/faq"
                        data-testid="outils-cta-faq"
                        className="btn-primary rounded-full inline-block px-7 py-3.5 text-sm tracking-[0.15em] uppercase"
                    >
                        Questions fréquentes
                    </Link>
                    <Link
                        to="/ressources"
                        data-testid="outils-cta-ressources"
                        className="btn-secondary rounded-full inline-block px-7 py-3.5 text-sm tracking-[0.15em] uppercase"
                    >
                        Voir les ressources
                    </Link>
                </div>
            </div>
        </PageLayout>
    );
}
